import React, { ReactElement, useEffect, useState } from 'react';
import { GeneralDetails } from './general-details/GeneralDetails';
import { ActionConditions } from './action-conditions/ActionConditions';
import { RewardConditions } from './reward-conditions/RewardConditions';
import { PlayerManagement } from './player-management/PlayerManagement';
import { useAppStateContext } from 'src/AppState/AppState';
import { CAMPAIGN_LIST_ROOT, CAMPAIGN_ROOT } from 'src/domains/casino/utils/test-variables';
import { CampaignViewManager } from 'src/domains/casino/webview/bonus-engine/campaign/CampaignViewManager';
import { observer } from 'src/utils/mobx-react';
import { Button } from 'src/domains/common/components/button/button';
import { ArrowRightIcon } from 'lucide-react';
import { CampaignCreatorState } from 'src/domains/casino/webview/bonus-engine/campaign/CampaignCreator.state';
import { useCommon } from 'src/AppState/Common';
import { autorun } from 'src_common/common/mobx-wrapper';
import { CampaignTabList } from './CampaignTabList';
import { LoadingV2 } from 'src/components/Loading/LoadingV2';
import { CampaignHeader } from './CampaignHeader';
import { PromoCodes } from 'src/domains/casino/webview/bonus-engine/campaign/promo-code/PromoCodes';

export const Campaign = observer('Campaign', (): ReactElement | null => {
    const { routerBackoffice } = useAppStateContext();
    const common = useCommon();

    // Timestamp is used to create a new campaign when the ID is not provided in the route. We need it to add new state to weakMap. Use state to avoid create new key on re-render.
    // CampaignCreatorState should be change to use state instead of weak map.
    const [timestamp] = useState(Date.now().toString());
    const campaignIdInRoute =
        routerBackoffice.bonusCampaignParams?.type === 'EDIT'
            ? routerBackoffice.bonusCampaignParams.campaignId
            : (routerBackoffice.bonusCampaignParams?.cloneId ?? `default-id-${timestamp}`);
    const createCampaignState = CampaignCreatorState.get(common, campaignIdInRoute);
    const [campaignState] = useState(() => new CampaignViewManager(routerBackoffice, common, createCampaignState));
    const { handlePrevView, handleNextView, currentViewIndex, campaignViewsList } = campaignState;

    useEffect(() => {
        const dispose = autorun(() => {
            createCampaignState
                .fillDataIf()
                .then()
                .catch((e) => console.error(e));
        });

        return () => {
            dispose();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div
            className='flex flex-col flex-auto max-h-screen gap-6 p-6 overflow-x-hidden overflow-y-auto bg-white text-foreground flex-nowrap font-tertiary'
            data-test={CAMPAIGN_ROOT}
            style={{ scrollbarGutter: 'stable' }}
        >
            <CampaignHeader
                campaignId={campaignIdInRoute}
                campaignState={campaignState}
                routerBackoffice={routerBackoffice}
            />
            <CampaignTabList
                campaignId={campaignIdInRoute}
                campaignState={campaignState}
            />
            {createCampaignState.isLoading ||
            (createCampaignState.campaign === null && routerBackoffice.bonusCampaignParams?.type === 'EDIT') ? (
                <LoadingV2
                    dataTest={CAMPAIGN_LIST_ROOT}
                    title='Loading campaign...'
                />
            ) : (
                <form>
                    {routerBackoffice.bonusCampaignParams?.view === 'general-details' && (
                        <GeneralDetails campaignId={campaignIdInRoute} />
                    )}
                    {routerBackoffice.bonusCampaignParams?.view === 'promo-code' && (
                        <PromoCodes campaignId={campaignIdInRoute} />
                    )}
                    {routerBackoffice.bonusCampaignParams?.view === 'action-conditions' && (
                        <ActionConditions campaignId={campaignIdInRoute} />
                    )}
                    {routerBackoffice.bonusCampaignParams?.view === 'reward-conditions' && (
                        <RewardConditions campaignId={campaignIdInRoute} />
                    )}
                    {routerBackoffice.bonusCampaignParams?.view === 'player-management' && (
                        <PlayerManagement campaignId={campaignIdInRoute} />
                    )}
                </form>
            )}

            <div className='flex justify-between'>
                <Button
                    disabled={currentViewIndex <= 0}
                    icon='arrow-left'
                    onClick={handlePrevView}
                    variant='outline'
                >
                    Previous Tab
                </Button>

                <Button
                    disabled={currentViewIndex >= campaignViewsList.length - 1}
                    onClick={handleNextView}
                    variant='outline'
                >
                    Next Tab
                    <ArrowRightIcon className='size-4' />
                </Button>
            </div>
        </div>
    );
});
